import { useMutation, useQuery } from '@apollo/client';
import { ListAlt } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Typography,
  TypographyProps,
} from '@mui/material';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { filter, propEq } from 'ramda';
import { ChangeEvent, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import { TaskStatus, TaskItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { WidgetContentEmpty } from '~/components/WidgetContentEmpty/WidgetContentEmpty';

import { NEGOTIATION_TASK_RESOLVE_MUTATION } from './NegotiationTaskResolveMutation.gql';
import { NEGOTIATION_TASKS_QUERY } from './NegotiationTasksWidget.gql';

const emptyNodes: any[] = [];

const filterStatusMap = {
  true: propEq<TaskStatus, keyof TaskItem>(TaskStatus.OPEN, 'status'),
  false: propEq<TaskStatus, keyof TaskItem>(TaskStatus.DONE, 'status'),
};

const styleMap: { [key in TaskStatus]: TypographyProps['sx'] } = {
  [TaskStatus.OPEN]: undefined,
  [TaskStatus.DONE]: { textDecoration: 'line-through' },
};

export const NegotiationTasksWidget = () => {
  const { negotiationId } = useNegotiationDetail();
  const { onError } = useCustomSnackbar();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationTasksWidget' });
  const { t: tTasks } = useTranslation(I18nNamespace.tasks);
  const [showActive, setShowActive] = useState(true);
  const [widgetRef, active] = useScrollIntoView({ hash: 'issues' });

  const { data, loading } = useQuery(NEGOTIATION_TASKS_QUERY, {
    variables: {
      limit: 1000,
      offset: 0,
      where: {
        //
        // variant: { _eq: TaskVariant.TODO },
        relatedResourcesProject: { _eq: 'clara' },
        relatedResourcesVariant: { _eq: 'negotiation' },
        relatedResourcesReference: { _eq: negotiationId },
      },
    },
  });

  const [resolveTask] = useMutation(NEGOTIATION_TASK_RESOLVE_MUTATION, { onError });

  const nodes = data?.tasks.nodes || emptyNodes;

  const nodesFiltered = useMemo(() => {
    if (nodes) {
      return filter(filterStatusMap[`${showActive}`], nodes);
    }
    return [];
  }, [nodes, showActive]);

  const isEmpty = data !== undefined && nodesFiltered.length === 0;

  const handleItemCheck = (id: string) => () => {
    (async () => {
      await resolveTask({
        variables: { id },
      });
    })();
  };

  const renderItem = (item: TaskItem) => {
    const { id, message, status } = item;
    return (
      <ListItem key={id} sx={{ bgcolor: 'grey.100', borderRadius: 2 }}>
        <ListItemText
          disableTypography
          primary={
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              <Checkbox
                checked={status === TaskStatus.DONE}
                onChange={status === TaskStatus.OPEN ? handleItemCheck(id) : undefined}
              />
              <Typography variant="body2" sx={styleMap[status]}>
                {tTasks(message?.variant || '')}
              </Typography>
            </Stack>
          }
        />
      </ListItem>
    );
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowActive(event.target.checked);
  };

  return (
    <>
      <Widget ref={widgetRef} active={active} data-intercom-target="NegotiationTasksWidget">
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<ListAlt fontSize="small" />} />}
          actionsLeft={loading ? <CircularProgress size={20} /> : undefined}
          actionsRight={<Switch checked={showActive} onChange={handleChange} />}
        />

        {isEmpty ? (
          <WidgetContentEmpty title={t('empty')} />
        ) : (
          <WidgetContent>
            <List>{nodesFiltered.map(renderItem)}</List>
          </WidgetContent>
        )}
      </Widget>
    </>
  );
};
